import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Autocomplete, AutocompleteOption } from '@/components/ui/custom/Autocomplete/Autocomplete';
import useDialogs from '@/hooks/useDialogs';
import { TagChips } from '@/modules/tryIt/shared/TagChips';
import { selectAllTags } from '@/redux/dialogs/selectors';
import { MAX_CONVERSATION_TAGS } from '@/util/constants';
import { newTagSchema } from '@/util/validation/schemas/dialog-schemas';

import { useContextTags } from '../../../../hooks/useContextTags';
import { selectBrainId } from '../../../../redux/selectors';

/**
 * "Tags" editor for the test-mode Try It. A searchable combobox suggests the
 * brain's existing tags and lets the user type a new one (validated by
 * `newTagSchema`); selected tags render as removable chips and persist to
 * `context.tags`. Mirrors the legacy `TagsSection` behavior and cap.
 */
export const TagsSection = () => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { dialogs } = useDialogs(brainId);
  const { tags, addTag, removeTag } = useContextTags();

  // Suggest the brain's existing tags that aren't already applied.
  const suggestions = useMemo<AutocompleteOption[]>(
    () =>
      selectAllTags(dialogs ?? [])
        .filter((tag) => !tags.includes(tag))
        .map((tag) => ({
          value: tag,
          label: tag,
          category: t('dialog.tags.auto_complete_group_by'),
        })),
    [dialogs, tags, t]
  );

  // Add either a picked suggestion or a typed value, rejecting tags that fail
  // the shared name validation.
  const handleAdd = (value: string) => {
    if (value && newTagSchema.isValidSync({ tag: value })) {
      addTag(value);
    }
  };

  return (
    <div className="flex flex-col items-start gap-4 self-stretch">
      <div className="flex max-w-sm flex-col items-start gap-2 self-stretch">
        <span className="text-sm leading-5 font-medium text-foreground">
          {t('try_it.context.tags_label')}
        </span>
        <Autocomplete
          options={suggestions}
          value=""
          onChange={handleAdd}
          onCreateNew={handleAdd}
          disabled={tags.length >= MAX_CONVERSATION_TAGS}
          placeholder={t('common.select_dots')}
          searchPlaceholder={t('common.search_dots')}
          ariaLabel={t('try_it.context.tags_label')}
        />
      </div>
      {tags.length > 0 && <TagChips tags={tags} onDelete={removeTag} />}
    </div>
  );
};
